import { useCallback } from 'react'
import { Platform } from 'react-native'
import ReactNativeBlobUtil from 'react-native-blob-util'

import Snackbar from '@/components/snackbar/Snackbar'
import useGlobalTask from '@/hooks/useGlobalTask'
import log from '@/utils/datadog/log/log'
import trpc from '@/utils/trpc'
import { downloadBase64 } from '@/utils/webDownload'

const usePrintPdfAction = (fosterId: string, currentOrganizationId: string) => {
  const { runTask } = useGlobalTask()

  const pdfQuery = trpc.foster.pdf.useQuery(
    {
      id: fosterId,
      organizationId: currentOrganizationId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    {
      enabled: false,
    }
  )

  // Get foster name for filename
  const fosterQuery = trpc.foster.byId.useQuery(
    {
      id: fosterId,
      organizationId: currentOrganizationId,
    },
    {
      enabled: false,
    }
  )

  const openPDF = useCallback(async () => {
    try {
      await runTask(
        {
          id: `pdf-generation-${fosterId}`,
          title: 'Generating Cover Sheet',
          description:
            'Please wait while the Cover Sheet is being generated...',
          type: 'pdf-generation',
          metadata: { fosterId },
        },
        async () => {
          const data = await pdfQuery.refetch()
          const pdf = data.data

          if (pdfQuery.error) {
            log.error('There was a problem generating a PDF', pdfQuery.error)
            Snackbar.error('There was an error generating the PDF.')
            return
          }

          if (!pdf) {
            log.error('No PDF was returned')
            Snackbar.error('There was an error generating the PDF.')
            return
          }

          if (Platform.OS === 'web') {
            // Get foster name for filename
            const fosterData = await fosterQuery.refetch()
            const fosterName = fosterData.data?.name || 'foster'
            const filename = `${fosterName.replace(/[^a-zA-Z0-9]/g, '_')}_coversheet.pdf`

            downloadBase64(pdf, filename, 'application/pdf')
          } else {
            const filePath =
              ReactNativeBlobUtil.fs.dirs.CacheDir + `/${fosterId}.pdf`
            const mimeType = 'application/pdf'

            await ReactNativeBlobUtil.fs.writeFile(filePath, pdf, 'base64')

            if (Platform.OS === 'android') {
              ReactNativeBlobUtil.android.actionViewIntent(filePath, mimeType)
            } else if (Platform.OS === 'ios') {
              ReactNativeBlobUtil.ios.openDocument(filePath)
            }
          }
        }
      )
    } catch (error) {
      log.error('Error generating PDF', error as Error)
      Snackbar.error('There was an error generating the PDF.')
    }
  }, [fosterId, pdfQuery, runTask])

  return { openPDF }
}

export default usePrintPdfAction
